import { IS_TEST_MODE } from "../constants";
import { authHeaders } from "./api-client";

export type AllocationStrategy = "random" | "user_ids" | "subscription";

export type AbExperiment = {
  id: number;
  slug: string;
  name: string;
  description: string;
  enabled: boolean;
  targetPathname: string;
  variantALabel: string;
  variantBLabel: string;
  allocationStrategy: AllocationStrategy;
  userIdsA: number[];
  userIdsB: number[];
  subscriptionsA: string[];
  subscriptionsB: string[];
  createdAt: string;
  updatedAt: string;
  createdById: number | null;
};

export type AbVariant = "a" | "b";

export type AbAssignment = {
  experiment: AbExperiment;
  variant: AbVariant;
};

export type AbExperimentStats = {
  experimentId: number;
  variantA: { impressions: number; conversions: number };
  variantB: { impressions: number; conversions: number };
};

function assertFiniteId(value: number, name: string): void {
  if (!Number.isFinite(value)) throw new Error(`Invalid ${name}: ${value}`);
}

// ── Admin CRUD ───────────────────────────────────────────────────

export async function listAdminExperiments(): Promise<AbExperiment[]> {
  if (IS_TEST_MODE) return [];
  const res = await fetch("/api/admin/experiments", { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load experiments.");
  const data = (await res.json()) as Partial<{ experiments: AbExperiment[] }>;
  if (!Array.isArray(data.experiments)) throw new Error("Unexpected response format.");
  return data.experiments;
}

export async function createAdminExperiment(input: {
  slug: string;
  name: string;
  description?: string;
  targetPathname: string;
  variantALabel: string;
  variantBLabel: string;
  allocationStrategy?: AllocationStrategy;
  userIdsA?: number[];
  userIdsB?: number[];
  subscriptionsA?: string[];
  subscriptionsB?: string[];
}): Promise<AbExperiment> {
  if (IS_TEST_MODE) throw new Error("Not available in test mode.");
  const res = await fetch("/api/admin/experiments", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as Partial<{ error: string }>;
    throw new Error(data.error ?? "Failed to create experiment.");
  }
  const data = (await res.json()) as Partial<{ experiment: AbExperiment }>;
  if (!data.experiment) throw new Error("Unexpected response format.");
  return data.experiment;
}

export async function updateAdminExperiment(
  id: number,
  input: {
    name?: string;
    description?: string;
    enabled?: boolean;
    targetPathname?: string;
    variantALabel?: string;
    variantBLabel?: string;
    allocationStrategy?: AllocationStrategy;
    userIdsA?: number[];
    userIdsB?: number[];
    subscriptionsA?: string[];
    subscriptionsB?: string[];
  }
): Promise<AbExperiment> {
  assertFiniteId(id, "experiment id");
  if (IS_TEST_MODE) throw new Error("Not available in test mode.");
  const res = await fetch(`/api/admin/experiments/${encodeURIComponent(id)}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as Partial<{ error: string }>;
    throw new Error(data.error ?? "Failed to update experiment.");
  }
  const data = (await res.json()) as Partial<{ experiment: AbExperiment }>;
  if (!data.experiment) throw new Error("Unexpected response format.");
  return data.experiment;
}

export async function deleteAdminExperiment(id: number): Promise<void> {
  assertFiniteId(id, "experiment id");
  if (IS_TEST_MODE) return;
  const res = await fetch(`/api/admin/experiments/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to delete experiment.");
}

export async function loadAdminExperimentStats(id: number): Promise<AbExperimentStats> {
  assertFiniteId(id, "experiment id");
  if (IS_TEST_MODE) {
    return {
      experimentId: id,
      variantA: { impressions: 0, conversions: 0 },
      variantB: { impressions: 0, conversions: 0 },
    };
  }
  const res = await fetch(`/api/admin/experiments/${encodeURIComponent(id)}/stats`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load experiment stats.");
  const data = (await res.json()) as Partial<{ stats: AbExperimentStats }>;
  if (!data.stats) throw new Error("Unexpected response format.");
  return data.stats;
}

// ── User-facing ──────────────────────────────────────────────────

export async function fetchActiveExperiment(
  pathname: string,
  signal?: AbortSignal
): Promise<AbAssignment | null> {
  if (IS_TEST_MODE) return null;
  const res = await fetch(
    `/api/website/experiments/active?pathname=${encodeURIComponent(pathname)}`,
    { headers: authHeaders(), signal }
  );
  if (!res.ok) return null;
  const data = (await res.json()) as Partial<{ assignment: AbAssignment | null }>;
  return data.assignment ?? null;
}

export async function recordExperimentEvent(input: {
  experimentId: number;
  variant: AbVariant;
  event: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  if (IS_TEST_MODE) return;
  if (!Number.isInteger(input.experimentId) || input.experimentId <= 0) return;
  await fetch("/api/website/experiments/events", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  }).catch(() => undefined);
}
